// ============================================================
// FEATURE: Dancers — a tiny faceless BLOCKY "chill guy" who dances along
// the BOTTOM-LEFT of the page to whatever audio is currently playing AND
// stands on his own "dancing circle" — a COMPASS. He has no face — just
// small stacked geometric blocks (a boxy head, a torso block, blocky arm +
// leg segments) filled with soft, cool gradients (muted blues / teals /
// indigos / soft purples). He is small, low and unobtrusive — and reacts
// to the beat with a gentle, understated bob / sway / nod.
//
// THE DANCING CIRCLE / COMPASS: beneath and around him sits a circular
// platform styled like a compass — cardinal marks (N/E/S/W), a fine tick
// ring, and a rotating needle/bearing. The figure is the compass indicator:
// he ORIENTS — the needle (and his lean) seeks toward the cursor like a
// compass bearing — WHILE still grooving to the beat. So he BOTH points
// (compass) AND dances.
//
// HOW HE HEARS THE MUSIC: a SINGLE requestAnimationFrame loop polls
// audioBus.getLevel() (0..1 — the live loudness of WHICHEVER source
// currently owns the shared bus: StaticFM / MusicBumper / CassetteDeck).
// That same loop tracks the cursor and eases the compass bearing toward it.
// The smoothed level is written ONCE per frame to a CSS custom property
// (--beat) and the eased bearing to --bearing (deg) on the strip; the
// figure's + needle's CSS read them. When nothing is playing the level
// rests at the idle floor and he falls into a slow idle groove; with no
// cursor movement the needle slowly seeks/sweeps like a settling compass.
//
// LAYERING / SAFETY:
//   - position: fixed, pinned to the BOTTOM-LEFT of the viewport so the
//     dancer + his circle stay put while you scroll.
//   - pointer-events: none on the whole strip so it NEVER blocks clicks.
//   - z-index ABOVE page content but BELOW the dock/FloorToolbar (9000)
//     and well below the crash / x-ray (100000+).
//
// PERF: one rAF, started only while on-screen + tab-visible (whenVisible
// gate from the shared util). No per-figure timers. The loop writes two
// CSS variables; the GPU-composited keyframes/transforms do the rest.
// Honors prefers-reduced-motion (no rAF, gentle/near-still pose, needle
// holds at rest). The pointer listener is passive + window-level and is
// torn down with the loop.
//
// Accepts an optional `enabled` prop (default true) to mount/unmount the
// whole strip.
// ============================================================

import * as React from "react";
import { useRef, useEffect, useMemo } from "react";
import {
  whenVisible,
  prefersReducedMotion,
} from "../_shared/sketchFrame";
import audioBus from "../_shared/audioBus";
import "./dancers.css";

// How many faceless figures populate the strip (kept in the 5–8 range the
// brief asks for). They are spread evenly across the full page width.
const DANCER_COUNT = 1;

// Per-frame LERP toward the raw bus level so the figures read the beat without
// jittering on every noisy analyser sample. Higher = snappier / more reactive.
const LEVEL_LERP = 0.22;

// Typical music sits in the ~0.1–0.5 band (mean analyser bin / 255), so we
// scale the raw level up into the 0..1 motion range and clamp. Kept modest so
// the little figures stay chill and never thrash — they nod, they don't flail.
const LEVEL_GAIN = 2.0;

// A faint baseline so that even in dead silence the figures keep a slow idle
// sway rather than freezing stiff. The drive is max(idle, scaled-level).
const IDLE_FLOOR = 0.1;

// --- Compass bearing tuning ----------------------------------------------
// Per-frame easing toward the target bearing so the needle seeks the cursor
// like a real compass: it leads, overshoots a touch via the slow LERP, and
// settles. Lower = lazier / more "magnetic damping".
const BEARING_LERP = 0.08;

// When the cursor hasn't moved for a while, the compass shouldn't lock dead
// still — it slowly sweeps/seeks (a gentle hunting drift) like a settling
// needle. This is the idle sweep speed in degrees per second.
const IDLE_SWEEP_DPS = 9;

// How long (ms) without pointer movement before the needle relaxes back into
// its idle seeking sweep instead of holding the last cursor bearing.
const IDLE_AFTER_MS = 2600;

// Shortest-path angular lerp (degrees). Keeps the needle from spinning the
// long way around when the target wraps past ±180°.
function lerpAngle(from, to, t) {
  let d = ((to - from + 540) % 360) - 180; // wrapped delta in (-180, 180]
  return from + d * t;
}

// Deterministic per-dancer character so each figure is a little different but
// stable across renders (no Math.random in render). Returns motion params that
// stagger phase, tempo and reach so the crowd is never in lockstep.
function dancerProfile(i, count) {
  // Spread evenly across the width with a touch of per-index jitter.
  const t = count > 1 ? i / (count - 1) : 0;
  // A crowd spreads across the width; a lone dancer sits snug in the far-left
  // corner (low enough to hug the edge, high enough the compass doesn't clip).
  const leftPct = count > 1 ? 6 + t * 88 : 3;
  // A pseudo-random but DETERMINISTIC value in [0,1) seeded by index.
  const seed = ((i * 2654435761) % 1000) / 1000;
  return {
    leftPct,
    // Animation phase offset (s) so bobs/sways don't sync up.
    delay: -(seed * 2.2).toFixed(3),
    // Base idle tempo (s per cycle) — slow + varied so the rest pose breathes.
    period: (2.6 + seed * 1.6).toFixed(2),
    // Slight scale variation so they read as a small crowd, not clones. Kept
    // small — these are tiny little block guys.
    scale: (0.86 + seed * 0.26).toFixed(3),
    // Which way this figure leans first (mirrors the sway/limbs).
    flip: i % 2 === 0 ? 1 : -1,
    // Per-figure gradient hue offset so each block guy gets a slightly
    // different cool tone while staying cohesive with the crowd.
    hue: i,
  };
}

// A cool, cohesive set of gradient stop pairs (top → bottom) in muted blues /
// teals / indigos / soft purples. Each block guy is assigned one by index so
// the crowd is varied but stays in the same low-saturation cool family.
const FIG_GRADIENTS = [
  ["#6f8fd6", "#3b4e86"], // soft blue → indigo
  ["#5fb3c0", "#2f6f86"], // teal → deep teal
  ["#8a8fd6", "#4a4f8e"], // periwinkle → indigo
  ["#6fa9c9", "#36617f"], // sky → slate blue
  ["#9a8cd0", "#544a8a"], // soft purple → muted violet
  ["#62b0a8", "#2d6e6a"], // seafoam → pine teal
  ["#7d96cf", "#3f5184"], // cornflower → indigo
];

// The DANCING CIRCLE rendered as a COMPASS — a circular platform that sits
// under/around the figure. Static SVG geometry only: an outer bezel, a fine
// tick ring, the four cardinal marks (N/E/S/W), and a rotating needle whose
// bearing is driven entirely by the --bearing CSS variable (set per frame by
// the rAF). The needle is the compass indicator that "seeks" the cursor.
// Faceless + decorative; aria-hidden on the strip already.
function CompassCircle() {
  return (
    <svg
      className="azd-compass"
      viewBox="0 0 64 64"
      width="64"
      height="64"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Cool, low-saturation platform fill so the circle matches the crowd. */}
        <radialGradient id="azd-compass-face" cx="50%" cy="42%" r="62%">
          <stop offset="0%" stopColor="rgba(36, 48, 82, 0.55)" />
          <stop offset="70%" stopColor="rgba(20, 28, 52, 0.45)" />
          <stop offset="100%" stopColor="rgba(12, 18, 36, 0.30)" />
        </radialGradient>
        <linearGradient id="azd-needle-n" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8fb6ec" />
          <stop offset="100%" stopColor="#4a64a8" />
        </linearGradient>
        <linearGradient id="azd-needle-s" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#33406e" />
          <stop offset="100%" stopColor="#222c4e" />
        </linearGradient>
      </defs>

      {/* Platform face — the disc the figure stands on. Kept close to a true
          circle (only a gentle perspective squash) so it reads as a round
          compass dial, not a flat coin. */}
      <ellipse
        className="azd-compass-disc"
        cx="32"
        cy="32"
        rx="29"
        ry="29"
        fill="url(#azd-compass-face)"
        stroke="rgba(140, 175, 255, 0.30)"
        strokeWidth="1"
      />

      {/* The compass ring proper — drawn as a true circle in its own group so
          the tick ring + bearing read clearly; the whole group is squashed to
          match the platform's perspective. */}
      <g transform="translate(32 32) scale(1 1)">
        {/* Tick ring: 60 fine ticks, every 5th longer (a compass rose dial). */}
        <g className="azd-compass-ticks">
          {Array.from({ length: 60 }, (_, i) => {
            const major = i % 5 === 0;
            const a = (i / 60) * Math.PI * 2;
            const r1 = 27;
            const r2 = major ? 22 : 24.5;
            return (
              <line
                key={i}
                x1={Math.sin(a) * r1}
                y1={-Math.cos(a) * r1}
                x2={Math.sin(a) * r2}
                y2={-Math.cos(a) * r2}
                stroke={
                  major
                    ? "rgba(160, 190, 255, 0.55)"
                    : "rgba(150, 180, 255, 0.22)"
                }
                strokeWidth={major ? 1.2 : 0.8}
              />
            );
          })}
        </g>

        {/* Cardinal marks N / E / S / W. */}
        {[
          ["N", 0, -17.5],
          ["E", 17.5, 0],
          ["S", 0, 17.5],
          ["W", -17.5, 0],
        ].map(([label, x, y]) => (
          <text
            key={label}
            x={x}
            y={y}
            className={"azd-cardinal" + (label === "N" ? " azd-cardinal-n" : "")}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {label}
          </text>
        ))}

        {/* The rotating bearing/needle. Its rotation is bound to --bearing.
            The N (lit) half points the way the figure faces; the S half is
            dim. A center hub caps it. */}
        <g className="azd-needle">
          <polygon points="0,-19 3.4,0 0,4 -3.4,0" fill="url(#azd-needle-n)" />
          <polygon points="0,19 3.4,0 0,-4 -3.4,0" fill="url(#azd-needle-s)" />
        </g>
        <circle
          cx="0"
          cy="0"
          r="2.6"
          fill="#0f1730"
          stroke="rgba(160, 190, 255, 0.6)"
          strokeWidth="1"
        />
      </g>
    </svg>
  );
}

// A single faceless BLOCKY figure — small stacked rectangles (boxy head,
// torso block, blocky arm + leg segments) filled with a soft cool gradient.
// All motion is CSS; this only renders the static geometry, the per-figure
// gradient, and the custom properties that stagger it.
function Dancer({ profile }) {
  // Stable, unique gradient id per figure so multiple dancers don't collide.
  const gid = `azd-grad-${profile.hue}`;
  const [c0, c1] = FIG_GRADIENTS[profile.hue % FIG_GRADIENTS.length];
  const style = {
    left: profile.leftPct + "%",
    // Fed into the keyframes so each figure is offset / tempoed differently.
    "--delay": profile.delay + "s",
    "--period": profile.period + "s",
    "--fig-scale": profile.scale,
    "--flip": profile.flip,
  };
  return (
    <div className="azd-dancer" style={style} aria-hidden="true">
      {/* The dancing circle / compass platform sits BEHIND + under the figure. */}
      <CompassCircle />
      {/* Orient wrapper: leans the whole figure toward the compass bearing
          (so he "points") while the inner SVG keeps its beat bob/sway. */}
      <div className="azd-orient">
      <svg
        className="azd-figure"
        viewBox="0 0 32 44"
        width="32"
        height="44"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c0} />
            <stop offset="100%" stopColor={c1} />
          </linearGradient>
        </defs>
        <g fill={`url(#${gid})`}>
          {/* legs — short blocky segments (swing gently from the hip) */}
          <g className="azd-leg azd-leg-l">
            <rect x="11" y="33" width="4" height="10" rx="1" />
          </g>
          <g className="azd-leg azd-leg-r">
            <rect x="17" y="33" width="4" height="10" rx="1" />
          </g>
          {/* torso — a clean rectangular block */}
          <rect className="azd-torso" x="10" y="17" width="12" height="17" rx="2" />
          {/* arms — slim blocky segments (swing gently from the shoulder) */}
          <g className="azd-arm azd-arm-l">
            <rect x="5" y="18" width="4" height="13" rx="1" />
          </g>
          <g className="azd-arm azd-arm-r">
            <rect x="23" y="18" width="4" height="13" rx="1" />
          </g>
          {/* faceless head — a small boxy block, no features */}
          <rect className="azd-head" x="10" y="3" width="12" height="12" rx="2" />
        </g>
      </svg>
      </div>
    </div>
  );
}

function Dancers({ enabled = true }) {
  // The fixed strip — visibility-gate target AND the element the rAF writes the
  // shared --beat custom property onto.
  const stripRef = useRef(null);

  // rAF + smoothing bookkeeping (refs so the loop never triggers React renders).
  const rafId = useRef(null);
  const onScreen = useRef(false);
  const smoothed = useRef(0);

  // Compass bearing bookkeeping. We track the last known cursor position, the
  // currently-eased bearing (deg, 0 = up/N), the timestamp of the last pointer
  // move (to know when to fall back to the idle sweep), and the previous frame
  // time (for a frame-rate-independent idle sweep).
  const pointer = useRef({ x: null, y: null, t: 0 });
  const bearing = useRef(0);
  const lastTs = useRef(0);

  const reduce = prefersReducedMotion();

  // Stable, deterministic crowd. Recomputed only if the count changes.
  const profiles = useMemo(
    () =>
      Array.from({ length: DANCER_COUNT }, (_, i) =>
        dancerProfile(i, DANCER_COUNT)
      ),
    []
  );

  // --- The beat loop --------------------------------------------------------
  // ONE rAF. Polls the shared audio bus, smooths it, and writes a single CSS
  // variable that every dancer's keyframes consume. Started only while the
  // strip is on-screen + the tab is visible; never runs under reduced-motion.
  useEffect(() => {
    // Reduced motion or disabled: leave the figures in their gentle CSS idle
    // pose (driven by a static --beat:0) and never spin up a loop.
    if (reduce || !enabled) return undefined;

    const strip = stripRef.current;
    if (!strip) return undefined;

    // Where the compass needle/figure lives on screen, so we can aim it at the
    // cursor. The lone dancer is pinned bottom-left; the compass disc center is
    // roughly here. Recomputed each frame (cheap) so it survives resize/scroll.
    const compassOrigin = () => {
      const el = strip.querySelector(".azd-compass");
      if (el) {
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      }
      // Fallback to the strip's bottom-left region if the compass isn't found.
      const r = strip.getBoundingClientRect();
      return { x: r.left + 40, y: r.top + r.height / 2 };
    };

    const start = () => {
      if (rafId.current != null) return; // already running
      lastTs.current = 0;
      const tick = (ts) => {
        // --- Beat -----------------------------------------------------------
        // Poll WHATEVER source owns the bus. Safe to call unconditionally; it
        // returns 0 when nothing is playing and never throws.
        const raw = audioBus.getLevel(); // 0..1

        // Scale typical music (~0.1–0.5) into a punchier motion range, then keep
        // a slow idle groove alive via the floor so silence still sways gently.
        const driven = Math.min(1, Math.max(IDLE_FLOOR, raw * LEVEL_GAIN));

        // LERP toward the driven target so the crowd feels the beat without
        // jittering on every noisy analyser frame.
        const next =
          smoothed.current + (driven - smoothed.current) * LEVEL_LERP;
        smoothed.current = next;

        // --- Compass bearing ------------------------------------------------
        // Frame delta (s) for a frame-rate-independent idle sweep.
        const dt = lastTs.current ? (ts - lastTs.current) / 1000 : 0;
        lastTs.current = ts;

        const p = pointer.current;
        const idleNow = !p.t || ts - p.t > IDLE_AFTER_MS;

        if (!idleNow && p.x != null) {
          // Aim the bearing at the cursor: 0deg = needle points up (N), and we
          // rotate clockwise. atan2 gives the angle from the compass center to
          // the pointer; convert to a compass bearing (0 = up).
          const o = compassOrigin();
          const target =
            (Math.atan2(p.x - o.x, o.y - p.y) * 180) / Math.PI; // deg, 0 = up
          bearing.current = lerpAngle(bearing.current, target, BEARING_LERP);
        } else {
          // No recent cursor movement: let the needle slowly seek/sweep like a
          // settling compass rather than locking dead still.
          bearing.current = (bearing.current + IDLE_SWEEP_DPS * dt) % 360;
        }

        // The body lean is a SMALL, clamped nod in the bearing's direction —
        // the needle does the precise pointing; the figure just leans toward it
        // so he visibly "points" without tipping over. Wrap to (-180,180] so the
        // idle sweep doesn't snap the body around at the 360deg seam, then clamp.
        const wrapped = ((bearing.current + 540) % 360) - 180;
        const lean = Math.max(-14, Math.min(14, wrapped * 0.12));

        // Writes per frame — the GPU-composited keyframes/transforms do the rest
        // (--beat drives the dance, --bearing rotates the needle, --lean the body).
        strip.style.setProperty("--beat", next.toFixed(3));
        strip.style.setProperty("--bearing", bearing.current.toFixed(2) + "deg");
        strip.style.setProperty("--lean", lean.toFixed(2) + "deg");

        rafId.current = requestAnimationFrame(tick);
      };
      rafId.current = requestAnimationFrame(tick);
    };

    const stop = () => {
      if (rafId.current != null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      // Settle the crowd back toward the idle floor so a paused strip doesn't
      // freeze mid-spike.
      smoothed.current = IDLE_FLOOR;
      if (stripRef.current) {
        stripRef.current.style.setProperty("--beat", IDLE_FLOOR.toFixed(3));
      }
    };

    // Track the cursor so the compass can seek it. Passive + window-level; just
    // stamps the latest position + time into a ref (read by the rAF). Never
    // blocks scrolling and adds no per-move work beyond the assignment.
    const onPointerMove = (e) => {
      pointer.current.x = e.clientX;
      pointer.current.y = e.clientY;
      pointer.current.t = performance.now();
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });

    // Only run while the strip is actually on-screen AND the tab is foregrounded.
    const cleanup = whenVisible(
      strip,
      () => {
        onScreen.current = true;
        start();
      },
      () => {
        onScreen.current = false;
        stop();
      }
    );

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      cleanup();
      stop();
    };
  }, [reduce, enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={stripRef}
      className={"azd-strip" + (reduce ? " azd-reduced" : "")}
      style={{ "--beat": IDLE_FLOOR, "--bearing": "0deg", "--lean": "0deg" }}
      aria-hidden="true"
    >
      {/* faint CRT / scanline tint over the strip (purely decorative) */}
      <div className="azd-scanlines" />
      <div className="azd-stage">
        {profiles.map((p, i) => (
          <Dancer key={i} profile={p} />
        ))}
      </div>
    </div>
  );
}

export default Dancers;
